import { fixButtonLabel, SEVERITY_RANK, SEVERITY_TONE, type DocumentFix } from './documentFixes';

/**
 * The always-visible list of compliance fixes (reference column). Cards are a CONSISTENT colour; severity
 * (critical/important/suggestion) is shown by a coloured badge and mirrored by the document highlight. Located
 * fixes get "Jump to fix"; only auto-applicable ones get "Apply". Hovering a card activates its highlight.
 */
export function DocumentFixList({
  fixes,
  locatedIds,
  activeId,
  onActivate,
  onJump,
  onApply,
  onReopen,
}: {
  fixes: DocumentFix[];
  locatedIds: Set<string>;
  activeId: string | null;
  onActivate: (id: string | null) => void;
  onJump: (id: string) => void;
  onApply: (id: string) => void;
  /** Clicking a reference card reopens its (possibly closed) floating card in the document. */
  onReopen?: (id: string) => void;
}) {
  // Order by severity (critical → suggestion), then keep document order within a severity.
  const ordered = fixes
    .map((fix, i) => ({ fix, i }))
    .sort((a, b) => SEVERITY_RANK[a.fix.severity] - SEVERITY_RANK[b.fix.severity] || a.i - b.i)
    .map((x) => x.fix);

  return (
    <ul className="docs-fixlist">
      {ordered.map((fix) => {
        const tone = SEVERITY_TONE[fix.severity];
        const located = locatedIds.has(fix.id);
        return (
          <li
            key={fix.id}
            className={`docs-fixcard${activeId === fix.id ? ' is-active' : ''}`}
            style={{
              background: tone.bg,
              borderColor: activeId === fix.id ? tone.color : 'transparent',
              boxShadow: `inset 3px 0 0 ${tone.color}`,
              cursor: located ? 'pointer' : 'default',
            }}
            onMouseEnter={() => onActivate(fix.id)}
            onMouseLeave={() => onActivate(null)}
            onClick={() => located && onReopen?.(fix.id)}
          >
            <div className="docs-fixcard__top">
              <span className="docs-sevpill" style={{ color: '#fff', background: tone.color }}>
                {tone.label}
              </span>
              {!located && <span className="docs-fixcard__scope">whole document</span>}
            </div>
            <strong className="docs-fixcard__title">{fix.title}</strong>
            <p className="docs-fixcard__detail">{fix.detail}</p>
            {fix.basis && (
              <p className="docs-fixcard__basis">
                {fix.basis.act}
                {fix.basis.section ? ` · ${fix.basis.section}` : ''}
              </p>
            )}
            <p className="docs-fixcard__action">
              <span className="docs-fixcard__label">Fix</span> {fix.action}
            </p>
            <div className="docs-fixcard__btns">
              {located && (
                <button
                  type="button"
                  className="docs-fixbtn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onJump(fix.id);
                  }}
                >
                  Jump to fix
                </button>
              )}
              {fix.fix ? (
                <button
                  type="button"
                  className="docs-fixbtn docs-fixbtn--primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onApply(fix.id);
                  }}
                >
                  {fixButtonLabel(fix)}
                </button>
              ) : (
                <span className="docs-fixcard__manual">Needs a manual edit</span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
